import type {
  AccountAllocationGridAccount,
  AccountAllocationGridReport,
} from '@components/AccountAllocationGrid/types';
import { AllocationMethodologies } from '@generated/graphql';
import { CUSTOM_OVERRIDE_TOLERANCE } from '@ovComponents/4-module/widgets/portfolio/components/allocationTolerances';
import {
  isAssetClassAllocationModel,
  storedPortfolioFraction,
} from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import {
  accountAllocationFingerprint,
  type StagedAccountAllocations,
  stagedMembershipChange,
} from './accountAllocationStaging';
import type { StagedAccountAllocationsState } from './hooks/useStagedAccountAllocations';

export type ApplyBlockReason =
  | { code: 'MISSING_SNAPSHOT' }
  | { code: 'STALE_REPORT' }
  | { code: 'MEMBERSHIP_CHANGED'; added: string[]; removed: string[] }
  | { code: 'BASELINE_CHANGED' };

export type ApplyDecision =
  | { status: 'COMMIT'; staged: StagedAccountAllocations }
  | { status: 'ALREADY_APPLIED' }
  | { status: 'BLOCKED'; reason: ApplyBlockReason };

export type SettledStagedAccountAllocationsState = Exclude<
  StagedAccountAllocationsState,
  { status: 'LOADING' }
>;

const blocked = (reason: ApplyBlockReason): ApplyDecision => ({
  status: 'BLOCKED',
  reason,
});

const holdsStagedCell = (
  account: AccountAllocationGridAccount | undefined,
  financialProductId: string,
  stagedFraction: number,
  isAssetClass: boolean,
) => {
  const holding = account?.holdings.find(
    (held) => held.financialProduct.id === financialProductId,
  );
  const stored = holding && storedPortfolioFraction(holding, isAssetClass);
  return (
    stored !== undefined &&
    Math.abs(stored - stagedFraction) <= CUSTOM_OVERRIDE_TOLERANCE
  );
};

// Applying moves the household off its baseline, so a re-run after a commit
// that landed (but whose NEXT_STEP did not) must be recognised before the
// baseline check, or it would block a workflow that actually succeeded.
// Matching values only count under Customized: Unified ignores them.
const holdsStagedMatrix = (
  staged: StagedAccountAllocations,
  report: AccountAllocationGridReport,
) => {
  if (report.allocationMethodology !== AllocationMethodologies.Customized) {
    return false;
  }
  const isAssetClass = isAssetClassAllocationModel(report);
  const { subAccountIds, financialProductIds, fractions } = staged.matrix;

  return subAccountIds.every((subAccountId, row) => {
    const account = report.subAccounts.find(
      (candidate) => candidate.subAccount.id === subAccountId,
    );
    return financialProductIds.every((financialProductId, column) => {
      const stagedFraction = fractions[row][column];
      return (
        stagedFraction === null ||
        holdsStagedCell(
          account,
          financialProductId,
          stagedFraction,
          isAssetClass,
        )
      );
    });
  });
};

// Order matters: membership is checked before the baseline so an added account
// is named, and "already applied" before the baseline (see holdsStagedMatrix).
export const decideAccountAllocationApply = (
  state: SettledStagedAccountAllocationsState,
  report: AccountAllocationGridReport,
): ApplyDecision => {
  if (state.status === 'ABSENT') return blocked({ code: 'MISSING_SNAPSHOT' });
  if (state.status === 'STALE_REPORT') return blocked({ code: 'STALE_REPORT' });
  if (!state.baseline) return blocked({ code: 'MISSING_SNAPSHOT' });

  const { staged, baseline } = state;

  const membership = stagedMembershipChange(staged, report);
  if (membership) return blocked({ code: 'MEMBERSHIP_CHANGED', ...membership });

  if (holdsStagedMatrix(staged, report)) return { status: 'ALREADY_APPLIED' };

  if (accountAllocationFingerprint(report) !== baseline.fingerprint) {
    return blocked({ code: 'BASELINE_CHANGED' });
  }

  return { status: 'COMMIT', staged };
};
